/**
 * Group-booking quantity math, shared by the booking-create route,
 * createMeeting (seat stamping), the times route, and the embed widget.
 * Pure functions — no DB, no side effects, safe in client bundles.
 */

const DEFAULT_QUANTITY_BOOKING = { enabled: false, min: 1, max: 10 };

/**
 * Resolve a service's quantity_booking config, repairing invalid values.
 * max < min collapses to min so the stepper/clamp can never invert.
 */
export function resolveQuantityBooking(service) {
  const qb = service?.quantity_booking;
  const enabled = qb?.enabled === true;
  const min =
    Number.isInteger(qb?.min) && qb.min >= 1
      ? qb.min
      : DEFAULT_QUANTITY_BOOKING.min;
  let max =
    Number.isInteger(qb?.max) && qb.max >= 1
      ? qb.max
      : DEFAULT_QUANTITY_BOOKING.max;
  if (max < min) max = min;
  return { enabled, min, max };
}

/**
 * Clamp a requested party size for a service.
 * Enabled  -> quantity = seats = clamp(requested, min, max); invalid -> min.
 * Disabled -> quantity passes through (POS sends quantity > 1 for pricing)
 *             but seats stays 1 so capacity math is unchanged.
 */
export function clampQuantity(service, requested) {
  const qb = resolveQuantityBooking(service);
  const raw = Math.floor(Number(requested));
  const valid = Number.isFinite(raw) && raw >= 1;

  if (!qb.enabled) {
    return { quantity: valid ? raw : 1, seats: 1 };
  }

  const base = valid ? raw : qb.min;
  const seats = Math.min(Math.max(base, qb.min), qb.max);
  return { quantity: seats, seats };
}

/**
 * Display formatter for the quantity column (booking list, CSV, emails).
 *
 * - multiday with a party (order.seats > 1): "2 × 3 days" (people × days)
 * - multiday legacy (no seats): the plain day count, as today
 * - bundle with seats stamped (feature on): the people count
 * - bundle legacy (no seats): the slot count, as today
 * - regular: order.quantity (already the people count, or 1)
 */
export function formatOrderQuantity(bookingType, order) {
  const quantity = Number(order?.quantity) || 1;
  const seats = Math.max(1, Number(order?.seats) || 1);
  if (bookingType === "multiday") {
    return seats > 1 ? `${seats} × ${quantity} days` : `${quantity}`;
  }
  if (bookingType === "bundle" && order?.seats != null) {
    return `${seats}`;
  }
  return `${quantity}`;
}
